/**
 * Совместимый hook для subscription limits
 * Теперь использует Zustand store вместо локальных запросов
 * Это предотвращает дублирование запросов когда хук используется в нескольких компонентах
 */

import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  useSubscriptionLimitsStore,
  useIsModelAllowed,
  useIsThinkingModeAllowed,
  useCanGenerateImages,
  useCanGenerateVideo,
  useIsImageProviderAllowed,
  useIsVideoProviderAllowed,
  useGetImageProviderConfig,
  useGetVideoProviderConfig,
  useGetModelUsage,
  useCanUseModel
} from '../stores/useSubscriptionLimitsStore';
import { useTeamStore } from '../stores/useTeamStore';

export function useSubscriptionLimits() {
  const { t } = useTranslation('common');
  // Подписываемся на отдельные части store для оптимизации
  const data = useSubscriptionLimitsStore(state => state.data);
  const isLoading = useSubscriptionLimitsStore(state => state.isLoading);
  const error = useSubscriptionLimitsStore(state => state.error);
  const fetchLimits = useSubscriptionLimitsStore(state => state.fetchLimits);
  const incrementUsageOptimistic = useSubscriptionLimitsStore(state => state.incrementUsageOptimistic);
  const rollbackUsageOptimistic = useSubscriptionLimitsStore(state => state.rollbackUsageOptimistic);
  
  // Отслеживаем изменения режима команды
  const isTeamMode = useTeamStore(state => state.isTeamMode);
  const activeTeamId = useTeamStore(state => state.activeTeam?.id);
  const prevTeamModeRef = useRef<{ isTeamMode: boolean; teamId: string | undefined } | null>(null);

  // Загружаем данные если их нет (только один раз благодаря кэшу в store)
  // ВАЖНО: fetchLimits не в зависимостях, т.к. он стабильный и не должен вызывать повторные эффекты
  useEffect(() => {
    if (!data && !isLoading) {
      fetchLimits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading]); // fetchLimits намеренно исключён
  
  // Обновляем лимиты при изменении режима команды
  useEffect(() => {
    const prevState = prevTeamModeRef.current;
    const currentState = { isTeamMode, teamId: activeTeamId };
    
    // Сохраняем текущее состояние для следующего сравнения
    prevTeamModeRef.current = currentState;
    
    // Если это первый рендер, не делаем ничего (данные загрузятся выше)
    if (!prevState) return;
    
    // Если режим команды или активная команда изменились - обновляем лимиты
    if (prevState.isTeamMode !== isTeamMode || prevState.teamId !== activeTeamId) {
      fetchLimits(true); // forceRefresh = true для немедленного обновления
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeamMode, activeTeamId]); // fetchLimits намеренно исключён

  // Дополнительная проверка: если данные не соответствуют режиму команды, обновляем
  // Это нужно для случаев когда первый запрос завершился до того, как isTeamMode стал true
  useEffect(() => {
    if (data && !isLoading && isTeamMode && activeTeamId) {
      // В режиме команды tier должен быть 'pro' или выше
      // Если tier = 'free', значит данные загрузились до переключения в team mode
      if (data.subscription.tier === 'free') {
        fetchLimits(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isTeamMode, activeTeamId]); // fetchLimits намеренно исключён

  // Helper functions из хуков (these return functions, so they're safe to use)
  const isModelAllowedFn = useIsModelAllowed();
  const isThinkingModeAllowedFn = useIsThinkingModeAllowed();
  const canGenerateImagesFn = useCanGenerateImages();
  const canGenerateVideoFn = useCanGenerateVideo();
  const isImageProviderAllowedFn = useIsImageProviderAllowed();
  const isVideoProviderAllowedFn = useIsVideoProviderAllowed();
  const getImageProviderConfigFn = useGetImageProviderConfig();
  const getVideoProviderConfigFn = useGetVideoProviderConfig();
  const getModelUsageFn = useGetModelUsage();
  const canUseModelFn = useCanUseModel();

  // Create wrapper functions that directly access data without calling hooks
  // This allows them to be called from event handlers safely
  const canSendMessage = () => {
    if (!data) return { allowed: true };
    const limit = data.limits.chat.messagesPerDay;
    // undefined, null, NaN или -1 = безлимит (платные тарифы работают по токенам)
    if (limit === undefined || limit === null || limit === -1 || Number.isNaN(limit)) return { allowed: true };
    if (data.usage.messages >= limit) {
      return { allowed: false, reason: `Daily limit reached (${limit} messages)` };
    }
    return { allowed: true };
  };

  const canUseWebSearch = () => {
    if (!data) return { allowed: true };
    const limit = data.limits.chat.webSearchPerDay;
    // undefined, null, NaN или -1 = безлимит (платные тарифы работают по токенам)
    if (limit === undefined || limit === null || limit === -1 || Number.isNaN(limit)) return { allowed: true };
    if (data.usage.webSearches >= limit) {
      return { allowed: false, reason: `Web search limit reached (${limit}/${limit})` };
    }
    return { allowed: true, reason: `${data.usage.webSearches}/${limit} used` };
  };

  const canUseFiles = () => {
    if (!data) return { allowed: true };
    const limit = data.limits.chat.filesPerDay;
    // undefined, null, NaN или -1 = безлимит (платные тарифы работают по токенам)
    if (limit === undefined || limit === null || limit === -1 || Number.isNaN(limit)) return { allowed: true };
    if (data.usage.fileRequests >= limit) {
      return { allowed: false, reason: `File limit reached (${limit}/${limit})` };
    }
    return { allowed: true, reason: `${data.usage.fileRequests}/${limit} used` };
  };

  const canUseTranscription = () => {
    if (!data) return { allowed: true };
    if (!data.limits.chat.transcription) {
      return { allowed: false, reason: t('limits.transcriptionUnavailable') };
    }
    return { allowed: true };
  };

  const canUseDeepResearch = () => {
    if (!data) return { allowed: true };
    const limit = data.limits.chat.deepResearchPerDay;
    // undefined, null, NaN = недоступно (free tier)
    if (limit === undefined || limit === null || Number.isNaN(limit) || limit === 0) {
      return { allowed: false, reason: t('limits.deepResearchUnavailable') };
    }
    // -1 = безлимит
    if (limit === -1) return { allowed: true };
    
    const usage = data.usage.deepResearch || 0;
    if (usage >= limit) {
      return { allowed: false, reason: t('limits.deepResearchExhausted', { limit }) };
    }
    return { allowed: true, reason: `${usage}/${limit} ${t('limits.used')}` };
  };

  const canGenerateImage = (count: number = 1) => {
    if (!data) return { allowed: true };
    
    // Проверка дневного лимита
    const limit = data.limits.images.imagesPerDay;
    // undefined, null, NaN или -1 = безлимит (платные тарифы работают по токенам)
    if (limit === undefined || limit === null || limit === -1 || Number.isNaN(limit)) {
      return { allowed: true, current: data.usage.imagesGenerated, limit };
    }
    
    const currentUsage = data.usage.imagesGenerated || 0;
    if (currentUsage >= limit) {
      return { 
        allowed: false, 
        reason: t('limits.imageLimitExhausted', { limit }),
        current: currentUsage,
        limit: limit
      };
    }
    
    // Проверка, хватит ли лимита для запрошенного количества
    if (currentUsage + count > limit) {
      return {
        allowed: false,
        reason: t('limits.imageLimitInsufficient', { count, available: limit - currentUsage }),
        current: currentUsage,
        limit: limit,
        available: limit - currentUsage
      };
    }
    
    return { allowed: true, current: data.usage.imagesGenerated, limit };
  };

  return {
    data,
    loading: isLoading,
    error,
    refresh: fetchLimits,
    
    // Subscription info
    tier: data?.subscription.tier || 'free',
    
    // Check functions (now pure functions that don't use hooks)
    isModelAllowed: isModelAllowedFn,
    isThinkingModeAllowed: isThinkingModeAllowedFn,
    canSendMessage,
    canUseWebSearch,
    canUseFiles,
    canUseTranscription,
    canUseDeepResearch,
    canGenerateImage, // Проверка дневного лимита изображений
    canGenerateImages: canGenerateImagesFn,
    canGenerateVideo: canGenerateVideoFn,
    isImageProviderAllowed: isImageProviderAllowedFn,
    isVideoProviderAllowed: isVideoProviderAllowedFn,
    getImageProviderConfig: getImageProviderConfigFn,
    getVideoProviderConfig: getVideoProviderConfigFn,
    getModelUsage: getModelUsageFn,
    canUseModel: canUseModelFn,
    
    // Optimistic updates
    incrementUsageOptimistic,
    rollbackUsageOptimistic,
    
    // Usage data
    usage: data?.usage || {
      messages: 0,
      webSearches: 0,
      fileRequests: 0,
      voiceMessages: 0,
      modelUsage: {},
      imagesGenerated: 0,
      videosGenerated: 0
    },
    
    // Limits data
    limits: data?.limits,
    
    // Model mappings (единый источник истины с бэкенда)
    modelMappings: data?.modelMappings
  };
}

// Экспортируем типы для обратной совместимости
export type {
  SubscriptionTier,
  ChatLimits,
  ImageLimits,
  VideoLimits,
  Limits,
  Usage,
  SubscriptionLimitsData
} from '../stores/useSubscriptionLimitsStore';

